'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserRating {
  id?: string;
  userProfileId: string;
  driverId: string;
  bookingId: string;
  rating: number;
  reviewTitle?: string;
  reviewText?: string;
  professionalismRating?: number;
  punctualityRating?: number;
  careRating?: number;
  communicationRating?: number;
  isVerified?: boolean;
  isFeatured?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const userRatingService = {
  async createRating(rating: UserRating) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_ratings')
        .insert({
          user_profile_id: rating.userProfileId,
          driver_id: rating.driverId,
          booking_id: rating.bookingId,
          rating: rating.rating,
          review_title: rating.reviewTitle,
          review_text: rating.reviewText,
          professionalism_rating: rating.professionalismRating,
          punctuality_rating: rating.punctualityRating,
          care_rating: rating.careRating,
          communication_rating: rating.communicationRating,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        driverId: data.driver_id,
        bookingId: data.booking_id,
        rating: data.rating,
        reviewTitle: data.review_title,
        reviewText: data.review_text,
        professionalismRating: data.professionalism_rating,
        punctualityRating: data.punctuality_rating,
        careRating: data.care_rating,
        communicationRating: data.communication_rating,
        isVerified: data.is_verified,
        isFeatured: data.is_featured,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async getRatingsByProfileId(userProfileId: string): Promise<UserRating[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_ratings')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((rating: any) => ({
        id: rating.id,
        userProfileId: rating.user_profile_id,
        driverId: rating.driver_id,
        bookingId: rating.booking_id,
        rating: rating.rating,
        reviewTitle: rating.review_title,
        reviewText: rating.review_text,
        professionalismRating: rating.professionalism_rating,
        punctualityRating: rating.punctuality_rating,
        careRating: rating.care_rating,
        communicationRating: rating.communication_rating,
        isVerified: rating.is_verified,
        isFeatured: rating.is_featured,
        createdAt: rating.created_at,
        updatedAt: rating.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async getRatingByBookingId(bookingId: string): Promise<UserRating | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_ratings')
        .select('*')
        .eq('booking_id', bookingId)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        if (error.code === 'PGRST116') {
          return null;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        driverId: data.driver_id,
        bookingId: data.booking_id,
        rating: data.rating,
        reviewTitle: data.review_title,
        reviewText: data.review_text,
        professionalismRating: data.professionalism_rating,
        punctualityRating: data.punctuality_rating,
        careRating: data.care_rating,
        communicationRating: data.communication_rating,
        isVerified: data.is_verified,
        isFeatured: data.is_featured,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async updateRating(ratingId: string, updates: Partial<UserRating>) {
    const supabase = createClient();

    try {
      const updateData: any = {};

      if (updates.rating !== undefined) updateData.rating = updates.rating;
      if (updates.reviewTitle !== undefined) updateData.review_title = updates.reviewTitle;
      if (updates.reviewText !== undefined) updateData.review_text = updates.reviewText;
      if (updates.professionalismRating !== undefined) updateData.professionalism_rating = updates.professionalismRating;
      if (updates.punctualityRating !== undefined) updateData.punctuality_rating = updates.punctualityRating;
      if (updates.careRating !== undefined) updateData.care_rating = updates.careRating;
      if (updates.communicationRating !== undefined) updateData.communication_rating = updates.communicationRating;

      const { data, error } = await supabase
        .from('user_ratings')
        .update(updateData)
        .eq('id', ratingId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },
};